import { CellContext } from "@/lib/Components/DataTable/types";
import { useTranslations } from "use-intl";
import { RelocationListItem } from "@/app/Relocations/GraphQL/relocationListQuery";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  formatCurrency,
  formatDistance,
} from "@/lib/Formatters/formatCurrency";
import { inclusionIconMap } from "@/app/Relocations/Maps/inclusionIconMap";
import { getInclusionIconColor } from "@/app/Relocations/Components/RelocationTable";
import { cn } from "@/lib/utils";

/**
 * A gap rental has no relocation leg, so the deal reads as one block of bookable
 * units against the retail rate it is discounted from.
 */
export function GapRentalDealCell({
  row,
}: CellContext<RelocationListItem, any>) {
  const gapRental = row.original;
  const t = useTranslations("relocation");
  const tTable = useTranslations("relocation.table");

  return (
    <div className="space-y-1.5">
      <div className="text-xs">
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="cursor-help border-b border-dashed border-gray-400">
              <span className="font-medium text-gray-900">
                {gapRental.extra_hire_units_allowed}
              </span>
              <span className="ml-1 text-gray-500">
                {t(`hire_unit_type_plural.${gapRental.hire_unit_type}`)}
              </span>
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <div className="space-y-0.5 text-xs">
              <div className="flex justify-between gap-4">
                <span>{tTable("extra")}</span>
                <span className="font-medium">
                  {formatCurrency(
                    gapRental.extra_hire_unit_rate,
                    gapRental.currency,
                  )}
                </span>
              </div>
              {gapRental.retail_rate ? (
                <div className="flex justify-between gap-4">
                  <span>
                    {tTable("retailRate", {
                      rate: formatCurrency(
                        gapRental.retail_rate,
                        gapRental.currency,
                      ),
                    })}
                  </span>
                </div>
              ) : null}
            </div>
          </TooltipContent>
        </Tooltip>
      </div>

      <div className="text-xs">
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="cursor-help border-b border-dashed border-gray-400 font-medium text-gray-900">
              {formatDistance(
                gapRental.distance_allowed,
                gapRental.measurement,
              )}
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <div className="space-y-0.5 text-xs">
              <div className="flex justify-between gap-4">
                <span>
                  {tTable("perExtra", {
                    unit: t(`hire_unit_type.${gapRental.hire_unit_type}`),
                  })}
                </span>
                <span className="font-medium">
                  {formatDistance(
                    gapRental.extra_distance_allowed_per_extra_hire_unit ?? 0,
                    gapRental.measurement,
                  )}
                </span>
              </div>
              <div className="flex justify-between gap-4">
                <span>{tTable("excessRate")}</span>
                <span className="font-medium">
                  {formatCurrency(
                    gapRental.excess_distance_rate ?? 0,
                    gapRental.currency,
                  )}
                  /{t(`distanceUnit.${gapRental.measurement}`)}
                </span>
              </div>
            </div>
          </TooltipContent>
        </Tooltip>
      </div>

      {gapRental.inclusions.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {gapRental.inclusions.map(
            ({ id, type, value, has_free_tank, description }) => {
              const Icon = inclusionIconMap[type];

              let tooltipContent = "";
              if (has_free_tank) {
                tooltipContent = t("summaryCard.freeTank");
              } else {
                if (value) {
                  tooltipContent = formatCurrency(value, gapRental.currency);
                }
                if (description) {
                  tooltipContent +=
                    (tooltipContent ? " - " : "") +
                    description.replace(/<[^>]*>/g, "");
                }
              }

              return (
                <Tooltip key={id}>
                  <TooltipTrigger asChild>
                    <div className="cursor-help">
                      <Icon
                        className={cn("h-4 w-4", getInclusionIconColor(type))}
                      />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">{tooltipContent || "Inclusion"}</p>
                  </TooltipContent>
                </Tooltip>
              );
            },
          )}
        </div>
      ) : null}
    </div>
  );
}
